import React, { useState } from 'react';
import { BiX, BiUser, BiPhone, BiMapPin } from 'react-icons/bi';
import { BsTruck } from 'react-icons/bs';
import { FaMapPin } from 'react-icons/fa6';
import { Order } from '../../types/orders';
import toast from 'react-hot-toast';

interface OrderDetailsModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
  onContactCustomer: (orderId: string) => void;
  onContactRestaurant: (orderId: string) => void;
  onReassignRider: (orderId: string) => void;
  onEscalateIssue: (orderId: string) => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  isOpen,
  onClose,
  onContactCustomer,
  onContactRestaurant,
  onReassignRider,
  onEscalateIssue
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'flagged':
        return 'text-red-600 bg-red-100';
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'out_for_delivery':
        return 'text-blue-600 bg-blue-100';
      case 'preparing':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getProgressPercentage = () => {
    switch (order.status) {
      case 'pending': return 10;
      case 'assigned': return 20;
      case 'confirmed': return 30;
      case 'preparing': return 40;
      case 'ready': return 50;
      case 'picked_up': return 70;
      case 'out_for_delivery': return 85;
      case 'delivered': return 100;
      case 'flagged': return 60; // Example progress for flagged
      default: return 0;
    }
  };

  const handleActionClick = async (action: () => void) => {
    setIsLoading(true);
    try {
      await action();
    } catch (error) {
      toast.error('Action failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-teal-600">Order {order.orderNumber}</h2>
            <p className="text-gray-500 text-sm mt-1">
              Complete order details and tracking
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <BiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status and Progress */}
          <div className="flex items-center justify-between">
            <div>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(order.status)}`}>
                {order.status === 'flagged' ? 'Flagged' : order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
              {order.flaggedReason && (
                <p className="text-red-600 text-sm mt-1">Reason: {order.flaggedReason}</p>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold text-gray-900">{getProgressPercentage()}%</div>
              <div className="text-sm text-gray-500">
                Estimated Delivery Time: {order.estimatedDeliveryTime || '25 Mins'}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                order.status === 'flagged' ? 'bg-red-500' : 'bg-teal-600'
              }`}
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>

          {/* Customer and Restaurant Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Customer Information:</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <BiUser className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{order.customer.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BiPhone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{order.customer.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BiMapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{order.customer.address}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Restaurant Information:</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <BsTruck className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{order.restaurant.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BiPhone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{order.restaurant.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BiMapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{order.restaurant.address}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Route */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Delivery Route:</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <BsTruck className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Pick-up Location</h4>
                  <p className="text-sm text-gray-600">
                    {order.deliveryRoute.pickupLocation.address}
                  </p>
                </div>
              </div>

              <div className="ml-4 border-l-2 border-dashed border-gray-200 h-8"></div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaMapPin className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Delivery Location</h4>
                  <p className="text-sm text-gray-600">
                    {order.deliveryRoute.deliveryLocation.address}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Order Items:</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={item.id || index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-sm text-gray-900">{item.name} x{item.quantity}</span>
                  <span className="text-sm font-medium text-gray-900">₦{item.price.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Assigned Rider */}
          {order.rider && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Assigned Rider:</h3>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-teal-600 font-medium text-sm">
                    {order.rider.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{order.rider.name}</p>
                  <p className="text-sm text-gray-600">{order.rider.phone}</p>
                </div>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="flex justify-between items-center pt-4 border-t">
            <h3 className="text-lg font-semibold text-teal-600">Total:</h3>
            <span className="text-2xl font-bold text-teal-600">₦{order.total.toLocaleString()}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <button
              onClick={() => handleActionClick(() => onContactCustomer(order.id))}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Contact Customer
            </button>
            <button
              onClick={() => handleActionClick(() => onContactRestaurant(order.id))}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Contact Restaurant
            </button>
            <button
              onClick={() => handleActionClick(() => onReassignRider(order.id))}
              disabled={isLoading}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              Reassign Rider
            </button>
            <button
              onClick={() => handleActionClick(() => onEscalateIssue(order.id))}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              Escalate Issue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;